import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Printer, Undo2, GitBranch } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { getPoGroup, mergeBalance } from '../../lib/api';
import { printPoSummary } from '../../lib/poSummaryPrint';
import { fmtQty, showDate } from './phasedUtils';

const STATE_STYLE = {
  dispatched: 'bg-emerald-100 text-emerald-700', open: 'bg-amber-100 text-amber-700',
  reversed: 'bg-red-100 text-red-700', cancelled: 'bg-zinc-100 text-zinc-600',
};

const balanceText = (v) => {
  if (Math.abs(v) < 0.001) return '0 ✓';
  return v > 0 ? `${fmtQty(v)} short` : `+${fmtQty(-v)} over`;
};

const LinesTable = ({ lines }) => (
  <table className="w-full text-xs" data-testid="po-summary-lines">
    <thead className="bg-muted/60 text-[10px] uppercase tracking-wide text-muted-foreground">
      <tr>{['Product', 'Ordered', 'Dispatched', 'In progress', 'Closed short', 'Over / short'].map((h, i) => (
        <th key={h} className={`px-2 py-1.5 font-medium ${i ? 'text-right' : 'text-left'}`}>{h}</th>
      ))}</tr>
    </thead>
    <tbody>
      {lines.map((r, i) => (
        <tr key={i} className="border-b last:border-0">
          <td className="px-2 py-1.5">{r.product_name}<div className="font-mono text-[10px] text-muted-foreground">{r.supplier_product_code}</div></td>
          {['ordered', 'dispatched', 'in_progress', 'closed_short'].map((k) => <td key={k} className="px-2 py-1.5 text-right font-mono">{fmtQty(r[k])}</td>)}
          <td className={`px-2 py-1.5 text-right font-mono font-semibold ${Math.abs(r.outstanding) >= 0.001 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid={`po-summary-balance-${i}`}>
            {balanceText(r.outstanding)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Variance = ({ p }) => (p.planned_units == null ? null : (
  <span className={p.variances.length ? 'text-amber-700' : 'text-emerald-700'} data-testid={`po-summary-variance-${p.dispatch_number}`}>
    planned {fmtQty(p.planned_units)} → shipped {fmtQty(p.units)}
    {p.variances.map((v) => ` · ${v.product_name} ${v.diff > 0 ? '+' : ''}${fmtQty(v.diff)}`).join('')}
  </span>
));

const Adjustments = ({ items }) => (!items.length ? null : (
  <div className="rounded-lg border" data-testid="po-summary-adjustments">
    <div className="border-b bg-muted/60 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Over / short carried between parts</div>
    <ul className="divide-y text-xs">
      {items.map((a, i) => (
        <li key={i} className="px-3 py-1.5">
          <span className="font-medium">Part {a.from_part}</span> shipped {fmtQty(Math.abs(a.units))} {a.kind} on {a.product_name} →{' '}
          {a.to_dispatch_number
            ? <span className="text-primary">Part {a.to_part} (#{a.to_dispatch_number}) {a.units > 0 ? '+' : '−'}{fmtQty(Math.abs(a.units))}</span>
            : <span className="text-amber-700">no open part, stays {a.kind} on the PO</span>}
          <span className="ml-2 text-muted-foreground">{a.at ? new Date(a.at).toLocaleString('en-IE', { dateStyle: 'short', timeStyle: 'short' }) : ''} · {a.by}</span>
        </li>
      ))}
    </ul>
  </div>
));

const PartRow = ({ p, canMerge, merging, onMerge }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-2 text-xs last:border-0" data-testid={`po-summary-part-${p.dispatch_number}`}>
    <span className="font-semibold">Part {p.part_number}</span>
    <span className="font-mono text-primary">#{p.dispatch_number}</span>
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize ${STATE_STYLE[p.state]}`}>
      {p.state === 'open' && ['locked', 'ready'].includes(p.status) ? 'Ready' : p.state}
    </span>
    <span className="font-mono text-muted-foreground">{showDate(p.dispatch_date)}</span>
    <span className="font-mono">{fmtQty(p.units)} units</span>
    <span className="text-muted-foreground">{p.batches.length ? p.batches.map((b) => b.batch_code).join(', ') : 'not batched yet'}</span>
    <Variance p={p} />
    {canMerge && p.can_merge_back && (
      <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" disabled={merging} onClick={() => onMerge(p)} data-testid={`po-merge-back-${p.dispatch_number}`}>
        {merging ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Undo2 className="mr-1 h-3 w-3" />} Merge back
      </Button>
    )}
  </li>
);

export const PoSummaryDialog = ({ groupId, onOpenChange, canMerge, onChanged }) => {
  const [data, setData] = useState(null);
  const [merging, setMerging] = useState(false);
  const load = useCallback(() => {
    setData(null);
    getPoGroup(groupId).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Could not load the PO summary'));
  }, [groupId]);
  useEffect(() => { if (groupId) load(); }, [groupId, load]);

  const merge = async (p) => {
    if (!window.confirm(`Merge order #${p.dispatch_number} back into the order it came from? It will be removed and its quantities added back.`)) return;
    setMerging(true);
    try {
      const { data: res } = await mergeBalance(p.id);
      toast.success(`Merged back into #${res.merged_into.dispatch_number}`);
      onChanged?.();
      if (res.group_dissolved) onOpenChange(false); else load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not merge back');
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={!!groupId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="po-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitBranch className="h-5 w-5 text-primary" /> PO {data?.po_number || ''} summary</DialogTitle>
          <DialogDescription>{data ? `${data.customer_name} · original order #${data.original_dispatch_number}` : 'Loading…'}</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" /> : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${data.status === 'complete' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="po-summary-status">
                {data.status === 'complete' ? 'Complete' : 'In progress'}
              </span>
              <span className="text-sm" data-testid="po-summary-count"><b>{data.dispatch_count}</b> dispatch note(s) sent · <b>{data.open_count}</b> open</span>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => printPoSummary(data)} data-testid="po-summary-print"><Printer className="mr-1 h-4 w-4" /> Print</Button>
            </div>
            <div className="overflow-hidden rounded-lg border"><LinesTable lines={data.lines} /></div>
            <ul className="overflow-hidden rounded-lg border">
              {data.parts.map((p) => <PartRow key={p.id} p={p} canMerge={canMerge} merging={merging} onMerge={merge} />)}
            </ul>
            <Adjustments items={data.adjustments || []} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
